import { type PropsWithChildren } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  formatDate,
  roundPotentiallyInfiniteNumber,
} from "@features/mmm/utils/formatters";
import { formatMetric } from "@north-beam/nb-common/formatting";

import {
  getMetricTitle,
  type CompareBudgetData,
  type MetricBoxOption,
} from "./metric-box";

export const MetricBoxTooltip = <T,>({
  children,
  selectedMetric,
  dateRange,
  compareData,
  isHistorical,
  className,
}: PropsWithChildren<{
  selectedMetric: MetricBoxOption<T>;
  dateRange?: { startDate: string; endDate: string };
  compareData: CompareBudgetData;
  isHistorical?: boolean;
  className?: string;
}>) => {
  const { user } = useUser();
  const formatPercentage = (percentage: number) =>
    `${roundPotentiallyInfiniteNumber(Math.abs(percentage), 0)}%`;

  const formatType = ["cac", "spend"].includes(compareData.metric)
    ? "dollars"
    : "multiplier";

  const title = getMetricTitle(selectedMetric);
  return (
    <GenericTooltip
      placement="top"
      mouseEnterDelay={1}
      infoCircleClassName="h-3.5 w-3.5"
      className={clsx(className)}
      noInfoCircle
      content={
        <div className="flex flex-col gap-1.5 min-w-[200px]">
          <div className="flex items-center justify-between gap-10">
            <div className="flex items-center gap-2">
              <div className="text-v2-primary font-semibold">{title}</div>
            </div>
            <div
              className={clsx(
                "text-2xs font-semibold py-1 px-1.5 flex items-center gap-0.5 rounded-full",
                {
                  "text-alien bg-alien-2": compareData.isDesiredDirection,
                  "text-mars bg-mars-2": !compareData.isDesiredDirection,
                },
              )}
            >
              <i
                className={clsx(
                  "fa-regular",
                  compareData.metricChangePercentage > 0
                    ? "fa-arrow-up"
                    : "fa-arrow-down",
                )}
              />
              {formatPercentage(compareData.metricChangePercentage)}
            </div>
          </div>
          <div className="flex  justify-between text-sm">
            <div>This mix</div>
            <div className="font-semibold text-cosmic text-right">
              {selectedMetric.displayValue}
            </div>
          </div>
          {!isHistorical && (
            <div>
              <div className="flex items-center justify-between text-sm">
                <div>Prior period</div>
                <div className="font-semibold text-cosmic">
                  {formatMetric(compareData.historicalValue, formatType, {
                    code: user.currencies.currentCurrency,
                    precision: "approximate",
                  })}
                </div>
              </div>
              {dateRange && (
                <span className="text-gray+2 text-2xs">
                  {`${formatDate(dateRange.startDate)} - ${formatDate(
                    dateRange.endDate,
                  )}`}
                </span>
              )}
            </div>
          )}
        </div>
      }
    >
      {children}
    </GenericTooltip>
  );
};
